import { createFileRoute, Link } from "@tanstack/react-router";
import { FileLock2, ExternalLink } from "lucide-react";
import { KENTRO } from "@/lib/constants";

const DPO_URL =
  "https://www.privacidade.com.br/portal-de-privacidade?token=88047695397ac976534ac691a2b9eb2b";

export const Route = createFileRoute("/aviso-de-privacidade")({
  head: () => ({
    meta: [
      { title: "Aviso de Privacidade e Proteção de Dados — Kentro" },
      {
        name: "description",
        content:
          "Aviso de Privacidade da Kentro Sistemas conforme a LGPD: dados coletados, finalidades, compartilhamento, segurança, retenção e direitos do titular.",
      },
      { property: "og:title", content: "Aviso de Privacidade e Proteção de Dados — Kentro" },
      {
        property: "og:description",
        content:
          "Documento completo de proteção de dados pessoais da Barbalho Solutions Ltda. em conformidade com a LGPD.",
      },
      { property: "og:url", content: "https://kentro.com.br/aviso-de-privacidade" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://kentro.com.br/aviso-de-privacidade",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Aviso de Privacidade e Proteção de Dados — Kentro",
          url: "https://kentro.com.br/aviso-de-privacidade",
          inLanguage: "pt-BR",
          dateCreated: "2023-10-01",
          dateModified: "2023-11-09",
          version: "1.0",
          publisher: {
            "@type": "Organization",
            name: "Barbalho Solutions Ltda.",
            taxID: "43.406.507/0001-52",
            address: {
              "@type": "PostalAddress",
            streetAddress: "Alameda Rio Negro, 503, Sala 2020",
            addressLocality: "Barueri",
            addressRegion: "SP",
            postalCode: "06454-000",
              addressCountry: "BR",
            },
          },
        }),
      },
    ],
  }),
  component: AvisoPage,
});

function AvisoPage() {
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-14">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <FileLock2 className="size-3.5" /> LGPD · Versão 1.0
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
            Aviso de <span className="gradient-text">Privacidade</span> e Proteção de Dados
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Elaborado em 01/10/2023 · Atualizado em 09/11/2023.
          </p>
          <p className="mt-5 max-w-3xl text-base text-muted-foreground">
            A <strong className="text-foreground">Barbalho Solutions Ltda.</strong>, CNPJ
            43.406.507/0001-52, com sede na Alameda Rio Negro, 503, Sala 2020, Alphaville, Barueri/SP —
            CEP 06454-000 (“Controlador”), regula neste Aviso, de forma simples, transparente e
            objetiva, como seus dados pessoais (“Titular”) serão coletados, utilizados e protegidos.
          </p>
          <a
            href={DPO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
          >
            Portal do Encarregado de Proteção de Dados (DPO){" "}
            <ExternalLink className="size-3.5" />
          </a>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
        <nav aria-label="Sumário" className="mb-12 rounded-2xl border border-border bg-card/40 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Sumário
          </h2>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li><a className="hover:text-foreground" href="#dados">Quais dados são coletados sobre você</a></li>
            <li><a className="hover:text-foreground" href="#uso">Como utilizamos seus dados pessoais</a></li>
            <li><a className="hover:text-foreground" href="#compartilhamento">Com quem compartilhamos seus dados pessoais</a></li>
            <li><a className="hover:text-foreground" href="#seguranca">Como mantemos seus dados pessoais seguros</a></li>
            <li><a className="hover:text-foreground" href="#retencao">Por quanto tempo seus dados serão armazenados</a></li>
            <li><a className="hover:text-foreground" href="#direitos">Seus direitos como titular e como exercê-los</a></li>
            <li><a className="hover:text-foreground" href="#legislacao">Legislação aplicável e alterações</a></li>
            <li><a className="hover:text-foreground" href="#glossario">Glossário</a></li>
          </ol>
        </nav>

        <div className="space-y-12">
          <Section id="resumo" title="Quadro resumo">
            <dl className="grid gap-4 sm:grid-cols-2">
              <Row k="Agente de tratamento" v="Barbalho Solutions Ltda." />
              <Row k="Papel no tratamento" v="Controlador e Operador" />
              <Row
                k="Natureza dos dados"
                v="Dados pessoais fornecidos pelo Titular, de natureza cadastral, financeira e sensível (art. 5º, II da Lei 13.709/2018)."
              />
              <Row
                k="Compartilhamento"
                v="Operadores e fornecedores essenciais; autoridades/órgãos governamentais por força legal ou regulatória."
              />
              <Row
                k="Proteção"
                v="Medidas técnicas e administrativas adequadas à natureza dos dados tratados."
              />
              <Row
                k="Seus direitos"
                v="Confirmação, acesso, anonimização, correção, eliminação, portabilidade, bloqueio, oposição e compartilhamento."
              />
            </dl>
          </Section>

          <Section id="dados" title="1. Quais dados são coletados sobre você">
            <p>
              Para prestar serviços aos nossos clientes ou contratar colaboradores, coletamos
              algumas informações sobre você:
            </p>
            <h3>Dados fornecidos diretamente por você</h3>
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr><th>Serviço</th><th>Dados pessoais</th></tr>
                </thead>
                <tbody>
                  <tr><td>Recebimento de currículos</td><td>Nome, idade, endereço, telefone, e-mail, qualificação profissional, currículo.</td></tr>
                  <tr><td>Entrevista de emprego</td><td>Nome, telefone, qualificação profissional, currículo.</td></tr>
                  <tr><td>Contratação de colaboradores</td><td>Nome, cônjuge e dependentes (se houver), pai, mãe, CPF, RG, CTPS, e-mail, data de nascimento, endereço, telefone, PIS, dados de saúde (ASO), salário e dados bancários.</td></tr>
                  <tr><td>Wi-Fi para clientes</td><td>Endereço MAC, IP de conexão.</td></tr>
                  <tr><td>Prospecção de clientes</td><td>Nome, telefone e e-mail.</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              Em alguns casos será necessário coletar dados de crianças que possuam vínculo parental
              com você ou estejam sob sua responsabilidade legal — nesses casos será requerido
              consentimento expresso. É seu dever prestar informações corretas e atualizadas.
            </p>
            <h3>Dados fornecidos por terceiros</h3>
            <p>
              Conforme permitido por lei, podemos receber dados de parceiros, prestadores e outras
              fontes independentes, inclusive bases de dados públicas, para viabilizar a prestação
              dos nossos serviços.
            </p>
            <h3>Dados coletados automaticamente</h3>
            <p>
              Coletamos também, de forma automática, dados de conexão Wi-Fi e dados de navegação
              (cookies).
            </p>
          </Section>

          <Section id="uso" title="2. Como utilizamos seus dados pessoais">
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr><th>Base legal</th><th>Finalidade</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Execução de contratos ou diligências preliminares (art. 7º, V, LGPD)</td>
                    <td>Recebimento de currículo; entrevista de emprego; contratação; prospecção de clientes.</td>
                  </tr>
                  <tr>
                    <td>Cumprimento de obrigação legal ou regulatória (art. 7º, II, LGPD)</td>
                    <td>Guarda de registros de acesso ao Wi-Fi (art. 13 do Marco Civil); guarda de documentos trabalhistas e previdenciários.</td>
                  </tr>
                  <tr>
                    <td>Consentimento (art. 7º, I, LGPD)</td>
                    <td>Tratamento de dados de crianças; contatos externos de interesse do titular via mensagens eletrônicas.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="compartilhamento" title="3. Com quem compartilharemos seus dados pessoais">
            <ol>
              <li>Com prestadores de serviço para a contratação de funcionários.</li>
              <li>Com órgãos governamentais para cumprimento de obrigação legal.</li>
              <li>Em transações ou alterações societárias envolvendo o Controlador, para continuidade dos serviços.</li>
              <li>Mediante ordem judicial ou requerimento de autoridades administrativas competentes.</li>
            </ol>
            <p>
              Todos os prestadores estão sujeitos a contratos rigorosos que proíbem o uso ou
              divulgação não autorizada dos dados pessoais aos quais têm acesso.
            </p>
          </Section>

          <Section id="seguranca" title="4. Como mantemos seus dados pessoais seguros">
            <ul>
              <li>Proteção contra acesso não autorizado aos sistemas.</li>
              <li>Acesso restrito de pessoas específicas aos locais de armazenamento.</li>
              <li>Compromisso de sigilo absoluto por agentes, funcionários e parceiros que tratam dados.</li>
              <li>Programa de governança em privacidade, gerido pelo Encarregado pela Proteção de Dados Pessoais (DPO).</li>
            </ul>
            <p>
              Embora o Controlador adote os melhores esforços, nenhuma transmissão é totalmente
              segura. Em caso de suspeita de quebra de segurança, podemos suspender total ou
              parcialmente os serviços sem aviso prévio e garantiremos transparência sobre o ocorrido.
            </p>
          </Section>

          <Section id="retencao" title="5. Por quanto tempo seus dados serão armazenados">
            <p>
              Seus dados serão armazenados pelo período estritamente necessário ao atendimento de
              cada finalidade e/ou conforme os respectivos prazos legais e regulatórios.
            </p>
          </Section>

          <Section id="direitos" title="6. Seus direitos como titular e como exercê-los">
            <ul>
              <li>Confirmação da existência de tratamento.</li>
              <li>Acesso aos dados.</li>
              <li>Correção de dados incompletos, inexatos ou desatualizados.</li>
              <li>Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade.</li>
              <li>Portabilidade dos dados a outro fornecedor, mediante requisição expressa.</li>
              <li>Eliminação dos dados tratados com consentimento.</li>
              <li>Informações sobre as entidades públicas ou privadas com as quais o Controlador compartilhou seus dados.</li>
              <li>Informação sobre a possibilidade de não fornecer consentimento e suas consequências.</li>
              <li>Revogação do consentimento.</li>
            </ul>
            <p>
              As requisições são gratuitas e submetidas a uma validação de identidade. Para exercer
              seus direitos, fale com nosso DPO pelo{" "}
              <a className="text-primary-glow hover:underline" href={DPO_URL} target="_blank" rel="noopener noreferrer">
                Portal de Privacidade
              </a>{" "}
              ou pelo e-mail{" "}
              <a className="text-primary-glow hover:underline" href={`mailto:${KENTRO.email}`}>
                {KENTRO.email}
              </a>
              .
            </p>
            <p>
              Sua requisição pode ser legalmente rejeitada por motivos formais (ex.: impossibilidade
              de comprovação de identidade) ou legais (ex.: pedido de exclusão de dados cuja
              manutenção é livre exercício de direito). Nesses casos, apresentaremos justificativas
              razoáveis.
            </p>
          </Section>

          <Section id="legislacao" title="7. Legislação aplicável e alterações">
            <p>
              Este documento foi elaborado com base na Lei Federal nº 13.709/2018 — Lei Geral de
              Proteção de Dados (LGPD). O Controlador reserva-se o direito, a seu exclusivo
              critério, de modificar, alterar, acrescentar ou remover partes deste documento a
              qualquer momento.
            </p>
            <p className="text-sm text-muted-foreground">
              Elaborado em: 01/10/2023 · Atualizado em: 09/11/2023 · Versão 1.0
            </p>
          </Section>

          <Section id="glossario" title="Glossário">
            <dl className="space-y-4">
              <Term term="Anonimização" def="Processo pelo qual o dado perde a possibilidade de associação, direta ou indireta, a um indivíduo, considerados os meios técnicos razoáveis e disponíveis no momento do tratamento." />
              <Term term="ANPD" def="Autoridade Nacional de Proteção de Dados: órgão público responsável por zelar, implementar e fiscalizar o cumprimento da LGPD em todo o território nacional." />
              <Term term="Banco de dados" def="Conjunto estruturado de dados pessoais, estabelecido em um ou vários locais, em suporte eletrônico ou físico." />
              <Term term="Controlador" def="Pessoa natural ou jurídica, de direito público ou privado, a quem competem as decisões referentes ao tratamento de dados pessoais." />
              <Term term="Consentimento" def="Manifestação livre, informada e inequívoca pela qual o titular concorda com o tratamento de seus dados pessoais para uma finalidade determinada." />
              <Term term="Cookies" def="Arquivos enviados pelo servidor ao computador do usuário para identificá-lo e obter dados de acesso, permitindo personalizar a navegação." />
              <Term term="Dado pessoal" def="Qualquer informação relacionada a pessoa natural identificada ou identificável (nome, endereço, e-mail, IP, telefone, CPF, entre outros)." />
              <Term term="Dado pessoal sensível" def="Categoria especial de dados referentes a origem racial ou étnica, convicção religiosa, opinião política, filiação sindical, religião, saúde, vida sexual, dados genéticos ou biométricos." />
              <Term term="IP" def="Internet Protocol — conjunto de números que identifica o computador do usuário na internet." />
              <Term term="Operador" def="Pessoa natural ou jurídica que realiza o tratamento de dados pessoais em nome do controlador." />
              <Term term="Titular" def="Pessoa natural a quem se referem os dados pessoais (clientes, colaboradores, parceiros e terceiros)." />
              <Term term="Tratamento" def="Toda operação realizada com dados pessoais: coleta, produção, recepção, classificação, utilização, acesso, reprodução, transmissão, distribuição, processamento, arquivamento, armazenamento, eliminação, avaliação, controle, modificação, comunicação, transferência, difusão ou extração." />
            </dl>
          </Section>
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-card/40 p-6 md:p-8">
          <h2 className="text-lg font-semibold">Outros documentos</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/privacidade"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              Política de Privacidade da Plataforma
            </Link>
            <Link
              to="/ouvidoria"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              Canal de Denúncias
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
      <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-muted-foreground [&_a]:text-primary-glow [&_h3]:mt-6 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:my-1 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5 [&_strong]:text-foreground [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-lg [&_table]:border [&_table]:border-border [&_td]:border [&_td]:border-border [&_td]:p-3 [&_td]:align-top [&_th]:border [&_th]:border-border [&_th]:bg-card/60 [&_th]:p-3 [&_th]:text-left [&_th]:text-foreground [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="rounded-xl border border-border bg-card/40 p-4">
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k}</dt>
      <dd className="mt-1 text-sm text-foreground">{v}</dd>
    </div>
  );
}

function Term({ term, def }: { term: string; def: string }) {
  return (
    <div>
      <dt className="font-semibold text-foreground">{term}</dt>
      <dd className="text-sm text-muted-foreground">{def}</dd>
    </div>
  );
}
